import React, { useState, createContext, useEffect, useCallback } from 'react'
import Cookies from 'js-cookie'
import queryString from 'query-string'
import {
  useQuery,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { type DiscountCoupon } from '@mc/api'
import { DISCOUNT_COOKIE_KEY, DISCOUNT_VIEWED, ONE_WEEK } from '@mc/constants'
import { usePromotion, isPromoCode } from '@mc/promo-utils'
import { useHideCouponsIntroPricingExperiment } from '../../../experiments/prcng-338_v1_intro_pricing'
import { fetchDiscountData } from '../../../lib/helpers'
import { trackPromoModalCta, trackPromoModalViewed } from '../events'

interface ContextProviderProps {
  children: React.ReactNode
}

export interface DiscountModalContextValue {
  discount: DiscountCoupon | null | undefined
  isLoading: boolean
  showModal: boolean
  onClose: (action: string) => void
  query: queryString.ParsedQuery<string> | undefined
  pathname: string
}

export const queryClient = new QueryClient()

const DiscountModalContext = createContext<DiscountModalContextValue | null>(
  null,
)

const useShouldShowModal = (
  discountSlug: string,
  discountPreviouslyViewed: boolean,
) => {
  const shouldHideByExperiment =
    useHideCouponsIntroPricingExperiment(discountSlug)
  // https://masterclass-dev.atlassian.net/browse/COM-2027 - Certificate promo codes
  const isCertificatePromoCode =
    discountSlug.startsWith('nsf-') ||
    discountSlug.startsWith('moonshots-') ||
    discountSlug.startsWith('disrupt')

  return (
    !!discountSlug &&
    !discountPreviouslyViewed &&
    !isPromoCode(discountSlug) &&
    !shouldHideByExperiment &&
    !isCertificatePromoCode
  )
}

const DataContextProvider = ({ children }: ContextProviderProps) => {
  const [query, setQuery] = useState<queryString.ParsedQuery<string>>()
  const [pathname, setPathname] = useState('')
  const [discountSlug, setDiscountSlug] = useState('')
  const [discountPreviouslyViewed, setDiscountPreviouslyViewed] = useState(true)

  const { isPromotionOn } = usePromotion()

  useEffect(() => {
    const parsedQuery = queryString.parse(window.location.search)
    const queryParam = parsedQuery?.promo_id?.toString() || ''
    const cookie = Cookies.get(DISCOUNT_COOKIE_KEY) || ''
    const slug = queryParam || cookie
    setQuery(parsedQuery)
    setPathname(window.location.pathname)
    setDiscountSlug(slug)
    setDiscountPreviouslyViewed(
      sessionStorage.getItem(DISCOUNT_VIEWED) === slug,
    )
  }, [])

  // If promo flag is on we don't want to overwrite the DISCOUNT_COOKIE_KEY
  // with the query param
  useEffect(() => {
    const queryParam = query?.promo_id?.toString() || ''
    const cookie = Cookies.get(DISCOUNT_COOKIE_KEY) || ''
    if (!isPromotionOn && queryParam && cookie !== queryParam) {
      Cookies.set(DISCOUNT_COOKIE_KEY, queryParam, { expires: ONE_WEEK })
    }
  }, [isPromotionOn, query])

  const shouldShowModal = useShouldShowModal(
    discountSlug,
    discountPreviouslyViewed,
  )

  const { data: discount, isInitialLoading } = useQuery({
    queryKey: ['discount', discountSlug],
    queryFn: () => fetchDiscountData(discountSlug),
    enabled: shouldShowModal,
  })

  const showModal = shouldShowModal && (!!discount || isInitialLoading)

  useEffect(() => {
    if (discount) trackPromoModalViewed({ discount })
  }, [discount])

  const onClose = useCallback(
    (action: string) => {
      if (discount) trackPromoModalCta(action, discount)
      sessionStorage.setItem(DISCOUNT_VIEWED, discount?.slug || discountSlug)
      setDiscountPreviouslyViewed(true)
    },
    [discount, discountSlug],
  )

  return (
    <DiscountModalContext.Provider
      value={{
        discount,
        isLoading: isInitialLoading,
        showModal,
        onClose,
        query,
        pathname,
      }}
    >
      {children}
    </DiscountModalContext.Provider>
  )
}

export const DiscountModalContextProvider = ({
  children,
}: ContextProviderProps) => (
  <QueryClientProvider client={queryClient}>
    <DataContextProvider>{children}</DataContextProvider>
  </QueryClientProvider>
)

export const useDiscountModal = () => {
  const ctx = React.useContext(DiscountModalContext)
  if (!ctx) {
    throw new Error(
      'DiscountModalContext cannot be used outside of DiscountModalContextProvider',
    )
  }
  return ctx
}
